import React, { useState, useEffect, useMemo } from 'react';
import Layout from "../../../Component/Layout";
import AsyncSelect from "react-select/async";
import { ToastContainer, toast, Zoom } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import CharacteristicsModal from "../../../Component/CharacteristicsModal ";
import { ORDER_API } from "../../../apiConfig";
import { FileText } from 'lucide-react';

function CustomerSupport() {
    const [loading, setLoading] = useState(false);
    const [billingData, setBillingData] = useState([]);
    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const MANUAL_PATH = "/assets/files/Y(Single core)_Datasheet (2).pdf";
    
    // Filters
    const [filterCustomer, setFilterCustomer] = useState(null);
    
    // Checkbox & Modal State
    const [selectedBillingId, setSelectedBillingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalDetails, setModalDetails] = useState([]);
    const [activeBillingId, setActiveBillingId] = useState("");
    
    // Complaint Generation State
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Comment Modal State
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [submitComment, setSubmitComment] = useState("");

    // Tab & Char Modal State
    const [activeTab, setActiveTab] = useState("basic");
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);

    useEffect(() => {
        fetchBillingSummary();
    }, []);

    const fetchBillingSummary = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/customer-support/summary`);
            if (res.ok) {
                const data = await res.json();
                setBillingData(data.data);
            } else {
                toast.error("Failed to load billing summary");
            }
        } catch (err) {
            console.error("Failed to load billing summary:", err);
            toast.error("Error fetching data");
        } finally {
            setLoading(false);
        }
    };

    const loadCustomerOptions = async (inputValue) => {
        try {
            const res = await fetch(`${ORDER_API}/customer-support/customers`);
            const data = await res.json();
            const options = data.data.map(c => ({
                value: c.partner_code,
                label: `${c.partner_code} - ${c.vendor_name}`,
            }));

            if (!inputValue) return options;
            const search = inputValue.toLowerCase();
            return options.filter(opt => opt.label.toLowerCase().includes(search));
        } catch (err) {
            return [];
        }
    };

    const filteredData = useMemo(() => {
        return billingData.filter((item) => {
            return (
                filterCustomer === null || 
                item.customer === filterCustomer.label || 
                (item.customer && item.customer.includes(filterCustomer.label))
            );
        });
    }, [billingData, filterCustomer]);

    const handleCheckboxChange = (bi_id) => {
        setSelectedBillingId(prev => prev === bi_id ? null : bi_id);
    };

    const handleProductCheckboxChange = (id) => {
        setSelectedProductIds(prev => 
            prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
        );
    };

    const handleOpenModal = async (bi_id) => {
        try {
            const res = await fetch(`${ORDER_API}/customer-support/details/${bi_id}`);
            if (res.ok) {
                const data = await res.json();
                setModalDetails(data.data);
                setActiveBillingId(bi_id);
                setActiveTab("basic");
                setSelectedProductIds([]); 
                setShowModal(true);
            } else {
                toast.error("Failed to fetch billing details");
            }
        } catch (err) {
            console.error("Error fetching details:", err);
            toast.error("Error fetching details");
        }
    };

    const handleRaiseComplaint = () => {
        if (!selectedBillingId) {
            toast.warn("Please select a billing ID first", { transition: Zoom });
            return;
        }
        handleOpenModal(selectedBillingId);
    };

    const openCommentModal = () => {
        if (selectedProductIds.length === 0) {
            toast.warn("Please select at least one product to generate a complaint.");
            return;
        }
        setSubmitComment("");
        setShowCommentModal(true);
    };

    const handleGenerateComplaint = async () => {
        if (!submitComment.trim()) {
            toast.warn("Please enter a comment before submitting.");
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await fetch(`${ORDER_API}/customer-support/generate-complaint`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    bi_id: activeBillingId,
                    productIds: selectedProductIds,
                    comment: submitComment
                })
            });

            const data = await res.json();
            if (res.ok) {
                toast.success(`Complaint ${data.complaintId} generated successfully!`);
                setShowCommentModal(false);
                setShowModal(false);
                setSelectedBillingId(null);
            } else {
                toast.error(data.message || "Failed to generate complaint");
            }
        } catch (error) {
            console.error("Error saving complaint:", error);
            toast.error("Internal Server Error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenCharModal = (productData) => {
        let chars = [];
        try {
            chars = typeof productData.characteristics === 'string' 
                ? JSON.parse(productData.characteristics) 
                : productData.characteristics || [];
        } catch (e) {
            chars = [];
        }

        const modalData = chars.map(c => ({
            characteristic: c.characteristic,
            value: c.value !== undefined ? c.value : (productData[c.characteristic] || "")
        }));
        
        setSelectedCharacteristics(modalData);
        setModalVisible(true);
    };

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        return val.toLocaleString('en-IN');
    };

    const basicInfo = modalDetails.length > 0 ? modalDetails[0] : {};

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
    <div>
        <div style={styles.pageTitle}>Customer Support</div>
        <div style={styles.pageSubtitle}>Manage and view your data here</div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button 
            className="btn btn-dark" 
            style={styles.primaryBtn} 
            onClick={handleRaiseComplaint}
            disabled={!selectedBillingId}
        >
            + Raise Complaint
        </button>

                            <button
                        onClick={() => {
                            setManualExists(null);
                            setShowManual(true);
                            fetch(MANUAL_PATH, { method: "HEAD" })
                                .then((res) => {
                                    const contentType = res.headers.get("content-type") || "";
                                    setManualExists(res.ok && contentType.includes("pdf"));
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
            style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                color: "#3b82f6",
                fontSize: "14px",
                fontWeight: "600",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
        >
            <FileText size={18} />
            User Manual
        </button>
    </div>
</div>

{showManual && (
    <div
        onClick={() => setShowManual(false)}
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
        }}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            style={{
                background: "#ffffff",
                borderRadius: "12px",
                width: "97%",
                height: "96%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 20px",
                    borderBottom: "1px solid #e2e8f0",
                }}
            >
                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                    User Manual
                </h4>
                <button
                    onClick={() => setShowManual(false)}
                    style={{
                        border: "none",
                        background: "#f1f5f9",
                        borderRadius: "8px",
                        width: "32px",
                        height: "32px",
                        cursor: "pointer",
                        fontSize: "18px",
                        color: "#475569",
                        lineHeight: 1,
                    }}
                    title="Close"
                >
                    ×
                </button>
            </div>

                                    {manualExists === null && (
                            <div style={{
                                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: "14px"
                            }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && (
                            <iframe
                                src={`${MANUAL_PATH}#zoom=170`}
                                title="User Manual PDF"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                }}
                            />
                        )}
        </div>
    </div>
)}

            <div style={styles.filterCard}>
                <div className="row g-4">
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>Customer</label>
                        <AsyncSelect
                            cacheOptions
                            loadOptions={loadCustomerOptions}
                            defaultOptions
                            value={filterCustomer}
                            onChange={setFilterCustomer}
                            placeholder="Select Customer"
                            isClearable={true}
                            styles={{
                                control: (base) => ({ ...base, minHeight: 36, fontSize: '13px' }),
                                menu: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                    </div>
                </div>
            </div>

            <div style={styles.tableContainer}>
                {loading && <div style={{ padding: 20 }}>Loading…</div>}
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.erpTh, width: '40px', textAlign: 'center' }}></th>
                            <th style={styles.erpTh}>Billing ID</th>
                            <th style={styles.erpTh}>Customer Name</th>
                            <th style={styles.erpTh}>SO ID</th>
                            <th style={styles.erpTh}>Total Price</th>
                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length ? filteredData.map(item => (
                            <tr key={item.bi_id}>
                                <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedBillingId === item.bi_id}
                                        onChange={() => handleCheckboxChange(item.bi_id)}
                                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                    />
                                </td>
                                <td style={styles.erpTd}>{item.bi_id}</td>
                                <td style={styles.erpTd}>{item.customer}</td>
                                <td style={styles.erpTd}>{item.so_id}</td>
                                <td style={{ ...styles.erpTd, fontWeight: 500 }}>
                                    ₹ {formatCurrency(item.total_price)}
                                </td>
                                <td style={styles.erpTd}>
                                    <button 
                                        className="btn btn-outline-dark btn-sm"
                                        style={{ fontSize: 11 }}
                                        onClick={() => handleOpenModal(item.bi_id)}
                                    >
                                        View
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="6" className="text-center p-3 text-muted">No Records Found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Main Details Modal */}
            {showModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1200px", width: "90%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.2rem' }}>
                                Billing Details: {activeBillingId}
                            </div>
                            <button style={styles.iconBtn} onClick={() => setShowModal(false)}>✕</button>
                        </div>
                        
                        <div style={styles.modalBody}>
                            <div style={styles.tabs}>
                                {['basic', 'product'].map(t => (
                                    <button
                                        key={t}
                                        className={`btn ${activeTab === t ? "btn-secondary" : "btn-light"}`}
                                        onClick={() => setActiveTab(t)}
                                        style={styles.compactTabBtn}
                                    >
                                        {t.charAt(0).toUpperCase() + t.slice(1)}
                                    </button>
                                ))}
                            </div>

                            {activeTab === "basic" && (
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Billing ID</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.bi_id || activeBillingId}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Customer</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.customer || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Sales Organization</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.sales_organization || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Division</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.division || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Distribution Channel</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.distribution_channel || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Payment Terms</label>
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#f8f9fa' }}>
                                            {basicInfo.payment_terms || "-"}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "product" && (
                                <div style={{ ...styles.tableContainer, padding: 0, overflowX: 'auto' }}>
                                    <table className="table" style={styles.erpTable}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                                <th style={{ ...styles.erpTh, width: '40px', textAlign: 'center' }}></th>
                                                <th style={{ ...styles.erpTh, width: '40px' }}>Item</th>
                                                <th style={styles.erpTh}>Product Code</th>
                                                <th style={styles.erpTh}>Description</th>
                                                <th style={styles.erpTh}>Class</th>
                                                <th style={styles.erpTh}>Batch Number</th>
                                                <th style={styles.erpTh}>UOM</th>
                                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Unit Price</th>
                                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Qty</th>
                                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Total Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {modalDetails.length > 0 ? modalDetails.map((row, idx) => (
                                                <tr key={idx} style={selectedProductIds.includes(row.id) ? { backgroundColor: '#f0f9ff' } : {}}>
                                                    <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                        <input 
                                                            type="checkbox"
                                                            checked={selectedProductIds.includes(row.id)}
                                                            onChange={() => handleProductCheckboxChange(row.id)}
                                                            style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                                        />
                                                    </td>
                                                    <td style={styles.erpTd}>{idx + 1}</td>
                                                    <td style={styles.erpTd}>{row.product_code}</td>
                                                    <td style={styles.erpTd}>{row.description}</td>
                                                    <td style={styles.erpTd}>
                                                        {row.product_class || 'N/A'}
                                                        <span 
                                                            style={{ cursor: 'pointer', marginLeft: 8, color: '#151616' }} 
                                                            onClick={() => handleOpenCharModal(row)}
                                                            title="View Characteristics"
                                                        >
                                                            ▶
                                                        </span>
                                                    </td>
                                                    <td style={{ ...styles.erpTd }}>
                                                        {row.batch_number || 'N/A'}
                                                    </td>
                                                    <td style={styles.erpTd}>{row.uom}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>₹ {formatCurrency(row.unit_price)}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{row.delivery_qty}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 600 }}>
                                                        ₹ {formatCurrency(row.total_price || (row.unit_price * row.delivery_qty))}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="10" className="text-center p-3 text-muted">No products found</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Close</button>
                            {activeTab === 'product' && (
                                <button 
                                    className="btn btn-dark" 
                                    onClick={openCommentModal}
                                    disabled={selectedProductIds.length === 0}
                                >
                                    Save
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Comment Modal */}
            {showCommentModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "600px", width: "100%" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600 }}>Submit Comment</div>
                            <button style={styles.iconBtn} onClick={() => setShowCommentModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <textarea
                                value={submitComment}
                                onChange={(e) => setSubmitComment(e.target.value)}
                                placeholder="Enter reason for complaint here..."
                                rows="4"
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    borderRadius: "5px",
                                    border: "1px solid #ccc",
                                    resize: "none"
                                }}
                            />
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => setShowCommentModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-dark"
                                onClick={handleGenerateComplaint}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CharacteristicsModal
                modalVisible={modalVisible}
                selectedCharacteristics={selectedCharacteristics}
                isEditing={false} 
                handleChange={() => {}} 
                handleClose={() => setModalVisible(false)}
            />

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
        </Layout>
    );
}

const styles = {
    compactLabel: { fontSize: "13px", fontWeight: 600, marginBottom: "4px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 26, fontWeight: 700, color: '#1e293b', marginBottom: 2 },
    pageSubtitle: { fontSize: 13, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default CustomerSupport;